import React, { memo, useEffect, useImperativeHandle, useRef } from "react";

import { createSlideShow, ISlideShow } from "../slideshow/slideshow-js";

import "../slideshow/slideshow.css";
import "./slideshow-preview.css";
import { ISpreadSlideShowItem } from "./spread-slideshow-item";

const CONFIRMED_SINGLE_CLICK_DELAY_MS = 300;

/** Use this type for typing the ref result */
export interface ISlidesShowPreviewRef {
	el: HTMLDivElement;
	previewEl: HTMLDivElement;
	slideShow: ISlideShow<ISpreadSlideShowItem>;
}

/**
 * Single-click side effect that must wait until the preview knows the first click
 * was not part of a double click.
 */
type ConfirmedSingleClickAction = () => void;

/**
 * Handles the immediate part of a preview click.
 *
 * Return `undefined` when the whole click handling is safe to run immediately.
 * Return a function when only the final single-click side effect must be delayed
 * until the double-click guard window expires.
 */
type SlideshowPreviewClickHandler = (e: MouseEvent) => ConfirmedSingleClickAction | undefined;

/**
 * Keep click and double click handling in one place so the preview component only
 * owns refs and slideshow setup.
 */
function useSingleOrDoubleClick(
	ref: React.RefObject<HTMLElement | null>,
	{
		onClick,
		onDoubleClick = () => undefined,
	}: {
		onClick?: SlideshowPreviewClickHandler;
		onDoubleClick?: (e: MouseEvent) => void;
	},
) {
	const pendingConfirmedSingleClickTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
		undefined,
	);
	// Real mobile browsers can still emit a native `dblclick` for a double tap.
	// Chrome DevTools device emulation may instead send two fast `click` events
	// with `detail === 1`, so we track time/position to synthesize the missing
	// double-click path only for that emulated flow.
	const lastSyntheticDoubleClickAtRef = useRef(0);
	const lastClickTimeRef = useRef(0);
	const lastClickPosRef = useRef({ x: 0, y: 0 });

	useEffect(() => {
		const previewRef = ref.current;
		if (!previewRef) return undefined;

		const cancelPendingConfirmedSingleClick = () => {
			if (!pendingConfirmedSingleClickTimeoutRef.current) return;
			clearTimeout(pendingConfirmedSingleClickTimeoutRef.current);
			pendingConfirmedSingleClickTimeoutRef.current = undefined;
		};

		const handleClick = (event: MouseEvent) => {
			const now = Date.now();
			const timeSinceLastClick = now - lastClickTimeRef.current;
			const dx = event.clientX - lastClickPosRef.current.x;
			const dy = event.clientY - lastClickPosRef.current.y;
			const distanceSq = dx * dx + dy * dy;

			lastClickTimeRef.current = now;
			lastClickPosRef.current = { x: event.clientX, y: event.clientY };

			// a synthetic double click is when two clicks come in with detail: 1 (like from DevTools emulated mobile)
			// within the double click delay, and within a reasonable distance of each other
			const isSyntheticDoubleClick =
				event.detail === 1 &&
				timeSinceLastClick < CONFIRMED_SINGLE_CLICK_DELAY_MS &&
				distanceSq < 2500;

			cancelPendingConfirmedSingleClick();

			if (event.detail > 1 || isSyntheticDoubleClick) {
				lastSyntheticDoubleClickAtRef.current = now;
				onDoubleClick(event);
				return;
			}

			if (!onClick) return;

			// `onClick` handles everything that is safe to do immediately.
			// A returned callback means "run this single-click side effect only if
			// no double click follows within the guard window".
			const confirmedSingleClickAction = onClick(event);

			if (!confirmedSingleClickAction) return;

			pendingConfirmedSingleClickTimeoutRef.current = setTimeout(() => {
				pendingConfirmedSingleClickTimeoutRef.current = undefined;
				confirmedSingleClickAction();
			}, CONFIRMED_SINGLE_CLICK_DELAY_MS);
		};

		const handleDoubleClick = (e: MouseEvent) => {
			// Clicked on the target?
			const eventPath = e.composedPath?.() ?? [];
			const clickedInsideTarget = eventPath.length
				? eventPath.includes(previewRef)
				: previewRef.contains(e.target as Node | null);

			if (!clickedInsideTarget) return;

			// Ignore a native `dblclick` that arrives shortly after we already synthesized
			// the same interaction from two emulated single clicks.
			if (
				Date.now() - lastSyntheticDoubleClickAtRef.current <
				CONFIRMED_SINGLE_CLICK_DELAY_MS
			) {
				return;
			}

			cancelPendingConfirmedSingleClick();
			onDoubleClick(e);
		};

		previewRef.addEventListener("click", handleClick);
		document.addEventListener("dblclick", handleDoubleClick);

		return () => {
			previewRef.removeEventListener("click", handleClick);
			document.removeEventListener("dblclick", handleDoubleClick);
			cancelPendingConfirmedSingleClick();
		};
	}, [onClick, onDoubleClick, ref]);
}

export const SlideshowPreview = memo(
	({
		onClick,
		onDoubleClick,
		onMouseMove,
		ref,
	}: {
		onClick?: SlideshowPreviewClickHandler;
		onMouseMove?: React.DOMAttributes<HTMLDivElement>["onMouseMove"];
		onDoubleClick: (e: MouseEvent) => void;
		ref?: React.Ref<ISlidesShowPreviewRef | null>;
	}) => {
		const slideShowElRef = useRef<HTMLDivElement>(null);
		const slideShowPreviewRef = useRef<HTMLDivElement>(null);

		// We have complex detection for single click vs double click
		// All is handled int this hook
		useSingleOrDoubleClick(slideShowPreviewRef, {
			onClick,
			onDoubleClick,
		});

		/** Expose SlideShow object via ref */
		useImperativeHandle(
			ref,
			() => {
				if (!slideShowElRef.current || !slideShowPreviewRef.current) {
					throw new Error("useImperativeHandle: No SlideShowPreview element");
				}

				const slideShow = createSlideShow(
					slideShowElRef.current,
				) as ISlideShow<ISpreadSlideShowItem>;

				// Make slideshow available on console in 'Debug' mode
				// Assuming only one slideshow item is used, else global variable overridden
				if (eXpress.isDebugMode) {
					(window as any)._slideShow = slideShow;
				}

				return {
					el: slideShowElRef.current,
					previewEl: slideShowPreviewRef.current,
					slideShow,
				};
			},
			[],
		);

		return (
			<div
				className="slideshow-preview"
				role="img"
				ref={slideShowPreviewRef}
				onMouseMove={onMouseMove}
			>
				{/* Parent fullscreen div needed for drag handles */}
				<div ref={slideShowElRef} />
			</div>
		);
	},
);
SlideshowPreview.displayName = "SlideshowPreview";
